import { useState } from "react";
import { useVeiculos } from "@/features/veiculos/hooks/useVeiculos";
import type { VeiculoListagemDto, VeiculoDto, SalvarVeiculoRequest } from "@/features/veiculos/types";
import { COMBUSTIVEL_OPTIONS, TIPO_FREIO_OPTIONS, CONDICAO_VEICULO_OPTIONS } from "@/features/veiculos/types";
import { veiculoService } from "@/features/veiculos/services/veiculoService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search, Truck } from "lucide-react";
import { toast } from "sonner";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

const EMPTY_FORM: SalvarVeiculoRequest = {
  placa: "", placaCidade: "", placaUf: "", renavam: "", chassi: "",
  chassiRemarcado: false, marca: "", modelo: "", ano: new Date().getFullYear(),
  anoModelo: new Date().getFullYear(), tipoCombustivel: 1, avaliacao: 0,
  temRastreador: false, rastreador: "", controlaKm: false, kmInicial: 0,
  frotaId: 0, faixaConsumoId: 0, observacao: "", isInativo: false,
  dtTrocaFluidoRadiador: null, dtLimpezaRadiador: null,
  codigoMarcaModelo: "", marcaVeiculoId: 0, modeloVeiculoId: 0,
  modeloCarroceriaId: 0, corId: 0, lotacao: "", eixos: 2, distanciaEixo: 0,
  tipoCarga: "", potenciaCv: 0, cilindradas: 0, tipoFreio: 1,
  condicaoVeiculo: 1, restricao: "", numeroMotor: "", motorista: "",
  pesoBruto: 0, pesoLiquido: 0,
  seguradora: "", exercicio: new Date().getFullYear(),
  valorCasco: 0, danosMaterialValor: 0, danosCorporaisValor: 0,
};

function dtoToForm(d: VeiculoDto): SalvarVeiculoRequest {
  return {
    placa: d.placa ?? "", placaCidade: d.placaCidade ?? "",
    placaUf: d.placaUf ?? "", renavam: d.renavam ?? "",
    chassi: d.chassi ?? "", chassiRemarcado: d.chassiRemarcado,
    marca: d.marca ?? "", modelo: d.modelo ?? "",
    ano: d.ano, anoModelo: d.anoModelo, tipoCombustivel: d.tipoCombustivel,
    avaliacao: d.avaliacao, temRastreador: d.temRastreador,
    rastreador: d.rastreador ?? "", controlaKm: d.controlaKm,
    kmInicial: d.kmInicial, frotaId: d.frotaId, faixaConsumoId: d.faixaConsumoId,
    observacao: d.observacao ?? "", isInativo: d.isInativo,
    dtTrocaFluidoRadiador: d.dtTrocaFluidoRadiador ? d.dtTrocaFluidoRadiador.substring(0, 10) : null,
    dtLimpezaRadiador: d.dtLimpezaRadiador ? d.dtLimpezaRadiador.substring(0, 10) : null,
    codigoMarcaModelo: d.codigoMarcaModelo ?? "", marcaVeiculoId: d.marcaVeiculoId,
    modeloVeiculoId: d.modeloVeiculoId, modeloCarroceriaId: d.modeloCarroceriaId,
    corId: d.corId, lotacao: d.lotacao ?? "", eixos: d.eixos,
    distanciaEixo: d.distanciaEixo, tipoCarga: d.tipoCarga ?? "",
    potenciaCv: d.potenciaCv, cilindradas: d.cilindradas, tipoFreio: d.tipoFreio,
    condicaoVeiculo: d.condicaoVeiculo, restricao: d.restricao ?? "",
    numeroMotor: d.numeroMotor ?? "", motorista: d.motorista ?? "",
    pesoBruto: d.pesoBruto, pesoLiquido: d.pesoLiquido,
    seguradora: d.seguradora ?? "", exercicio: d.exercicio,
    valorCasco: d.valorCasco, danosMaterialValor: d.danosMaterialValor,
    danosCorporaisValor: d.danosCorporaisValor,
  };
}

export default function Veiculos() {
  const [busca, setBusca] = useState("");
  const { query, obterPorId, criar, atualizar, excluir } = useVeiculos(busca || undefined);

  const [dialogOpen,  setDialogOpen]  = useState(false);
  const [excluirId,   setExcluirId]   = useState<number | null>(null);
  const [editingId,   setEditingId]   = useState<number | null>(null);
  const [loading,     setLoading]     = useState(false);
  const [form,        setForm]        = useState<SalvarVeiculoRequest>(EMPTY_FORM);

  const veiculos: VeiculoListagemDto[] = query.data?.itens ?? [];
  const paginacao = usePaginacaoLocal(veiculos);

  const setField = <K extends keyof SalvarVeiculoRequest>(k: K, v: SalvarVeiculoRequest[K]) =>
    setForm(f => ({ ...f, [k]: v }));

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = async (id: number) => {
    setLoading(true);
    try {
      const dto = await obterPorId(id);
      setEditingId(id);
      setForm(dtoToForm(dto));
      setDialogOpen(true);
    } catch {
      toast.error("Não foi possível carregar o veículo.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.placa.trim()) { toast.error("Placa é obrigatória."); return; }
    try {
      if (editingId) {
        await atualizar.mutateAsync({ id: editingId, req: form });
        toast.success("Veículo atualizado.");
      } else {
        await criar.mutateAsync(form);
        toast.success("Veículo cadastrado.");
      }
      setDialogOpen(false);
    } catch { /* global toast */ }
  };

  const handleExcluir = async () => {
    if (!excluirId) return;
    try {
      await excluir.mutateAsync(excluirId);
      toast.success("Veículo excluído.");
    } catch { /* global toast */ }
    setExcluirId(null);
  };

  const isSaving = criar.isPending || atualizar.isPending;

  if (query.isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="h-6 w-6" /> Veículos
          </h1>
          <p className="text-muted-foreground">Cadastro da frota de veículos</p>
        </div>
        <Button onClick={openNew}>
          <Plus className="h-4 w-4 mr-2" /> Novo Veículo
        </Button>
      </div>

      {/* Busca */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por placa, marca ou modelo..."
          className="pl-9"
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      {/* Tabela */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Placa</TableHead>
              <TableHead>Marca / Modelo</TableHead>
              <TableHead>Ano Fab.</TableHead>
              <TableHead>Ano Mod.</TableHead>
              <TableHead>Cidade / UF</TableHead>
              <TableHead className="text-right">KM Inicial</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[90px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map(v => (
              <TableRow key={v.id}>
                <TableCell className="font-mono font-medium">{v.placa ?? "—"}</TableCell>
                <TableCell>
                  {[v.marca, v.modelo].filter(Boolean).join(" / ") || "—"}
                </TableCell>
                <TableCell>{v.ano || "—"}</TableCell>
                <TableCell>{v.anoModelo || "—"}</TableCell>
                <TableCell>
                  {[v.placaCidade, v.placaUf].filter(Boolean).join(" / ") || "—"}
                </TableCell>
                <TableCell className="text-right">
                  {v.kmInicial > 0 ? v.kmInicial.toLocaleString("pt-BR") : "—"}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={v.isInativo ? "secondary" : "success"}>
                    {v.isInativo ? "Inativo" : "Ativo"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(v.id)} disabled={loading}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setExcluirId(v.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhum veículo encontrado para esta busca." : "Nenhum veículo cadastrado."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Veículo" : "Novo Veículo"}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="dados">
              <TabsList className="mb-4">
                <TabsTrigger value="dados">Dados Principais</TabsTrigger>
                <TabsTrigger value="fabrica">Inf. Fábrica</TabsTrigger>
                <TabsTrigger value="seguros">Seguros</TabsTrigger>
              </TabsList>

              {/* ── Tab: Dados Principais ─────────────────────────── */}
              <TabsContent value="dados" className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="placa">Placa *</Label>
                    <Input
                      id="placa"
                      maxLength={10}
                      value={form.placa}
                      onChange={e => setField("placa", e.target.value.toUpperCase())}
                      className="font-mono"
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="placaCidade">Cidade da Placa *</Label>
                    <Input
                      id="placaCidade"
                      maxLength={60}
                      value={form.placaCidade}
                      onChange={e => setField("placaCidade", e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="placaUf">UF *</Label>
                    <Input
                      id="placaUf"
                      maxLength={2}
                      value={form.placaUf}
                      onChange={e => setField("placaUf", e.target.value.toUpperCase())}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="renavam">RENAVAM *</Label>
                    <Input
                      id="renavam"
                      maxLength={60}
                      value={form.renavam}
                      onChange={e => setField("renavam", e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="chassi">Chassi *</Label>
                    <Input
                      id="chassi"
                      maxLength={60}
                      value={form.chassi}
                      onChange={e => setField("chassi", e.target.value.toUpperCase())}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="marca">Marca</Label>
                    <Input
                      id="marca"
                      maxLength={60}
                      value={form.marca}
                      onChange={e => setField("marca", e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="modelo">Modelo</Label>
                    <Input
                      id="modelo"
                      maxLength={60}
                      value={form.modelo}
                      onChange={e => setField("modelo", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="ano">Ano Fabricação</Label>
                    <Input
                      id="ano"
                      type="number"
                      min={1900}
                      max={2100}
                      value={form.ano}
                      onChange={e => setField("ano", parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="anoModelo">Ano Modelo</Label>
                    <Input
                      id="anoModelo"
                      type="number"
                      min={1900}
                      max={2100}
                      value={form.anoModelo}
                      onChange={e => setField("anoModelo", parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Combustível</Label>
                    <Select
                      value={String(form.tipoCombustivel)}
                      onValueChange={v => setField("tipoCombustivel", parseInt(v))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {COMBUSTIVEL_OPTIONS.map(o => (
                          <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="avaliacao">Avaliação (R$)</Label>
                    <Input
                      id="avaliacao"
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.avaliacao}
                      onChange={e => setField("avaliacao", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="kmInicial">KM Inicial</Label>
                    <Input
                      id="kmInicial"
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.kmInicial}
                      onChange={e => setField("kmInicial", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="dtTrocaFluidoRadiador">Próx. Troca Fluido Radiador</Label>
                    <Input
                      id="dtTrocaFluidoRadiador"
                      type="date"
                      value={form.dtTrocaFluidoRadiador ?? ""}
                      onChange={e => setField("dtTrocaFluidoRadiador", e.target.value || null)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="dtLimpezaRadiador">Próx. Limpeza Radiador</Label>
                    <Input
                      id="dtLimpezaRadiador"
                      type="date"
                      value={form.dtLimpezaRadiador ?? ""}
                      onChange={e => setField("dtLimpezaRadiador", e.target.value || null)}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="rastreador">Rastreador</Label>
                  <Input
                    id="rastreador"
                    maxLength={100}
                    value={form.rastreador}
                    onChange={e => setField("rastreador", e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="observacao">Observação</Label>
                  <Textarea
                    id="observacao"
                    maxLength={200}
                    rows={3}
                    value={form.observacao}
                    onChange={e => setField("observacao", e.target.value)}
                  />
                </div>

                <div className="flex flex-wrap gap-6 pt-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="temRastreador"
                      checked={form.temRastreador}
                      onCheckedChange={v => setField("temRastreador", v)}
                    />
                    <Label htmlFor="temRastreador">Possui Rastreador</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="controlaKm"
                      checked={form.controlaKm}
                      onCheckedChange={v => setField("controlaKm", v)}
                    />
                    <Label htmlFor="controlaKm">Controla KM</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="chassiRemarcado"
                      checked={form.chassiRemarcado}
                      onCheckedChange={v => setField("chassiRemarcado", v)}
                    />
                    <Label htmlFor="chassiRemarcado">Chassi Remarcado</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="isInativo"
                      checked={form.isInativo}
                      onCheckedChange={v => setField("isInativo", v)}
                    />
                    <Label htmlFor="isInativo">Inativo</Label>
                  </div>
                </div>
              </TabsContent>

              {/* ── Tab: Inf. Fábrica ─────────────────────────────── */}
              <TabsContent value="fabrica" className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="eixos">Qtd. Eixos</Label>
                    <Input
                      id="eixos"
                      type="number"
                      min={0}
                      value={form.eixos}
                      onChange={e => setField("eixos", parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="distanciaEixo">Distância Eixos (m)</Label>
                    <Input
                      id="distanciaEixo"
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.distanciaEixo}
                      onChange={e => setField("distanciaEixo", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="lotacao">Lotação</Label>
                    <Input
                      id="lotacao"
                      maxLength={10}
                      value={form.lotacao}
                      onChange={e => setField("lotacao", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="potenciaCv">Potência (CV)</Label>
                    <Input
                      id="potenciaCv"
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.potenciaCv}
                      onChange={e => setField("potenciaCv", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="cilindradas">Cilindradas</Label>
                    <Input
                      id="cilindradas"
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.cilindradas}
                      onChange={e => setField("cilindradas", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="tipoCarga">Tipo de Carga</Label>
                    <Input
                      id="tipoCarga"
                      maxLength={60}
                      value={form.tipoCarga}
                      onChange={e => setField("tipoCarga", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="pesoLiquido">Peso Líquido (kg)</Label>
                    <Input
                      id="pesoLiquido"
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.pesoLiquido}
                      onChange={e => setField("pesoLiquido", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="pesoBruto">Peso Bruto (kg)</Label>
                    <Input
                      id="pesoBruto"
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.pesoBruto}
                      onChange={e => setField("pesoBruto", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Tipo de Freio</Label>
                    <Select
                      value={String(form.tipoFreio)}
                      onValueChange={v => setField("tipoFreio", parseInt(v))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TIPO_FREIO_OPTIONS.map(o => (
                          <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label>Condição do Veículo</Label>
                    <Select
                      value={String(form.condicaoVeiculo)}
                      onValueChange={v => setField("condicaoVeiculo", parseInt(v))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONDICAO_VEICULO_OPTIONS.map(o => (
                          <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="codigoMarcaModelo">Código Marca/Modelo</Label>
                    <Input
                      id="codigoMarcaModelo"
                      maxLength={50}
                      value={form.codigoMarcaModelo}
                      onChange={e => setField("codigoMarcaModelo", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="numeroMotor">Número do Motor</Label>
                    <Input
                      id="numeroMotor"
                      maxLength={50}
                      value={form.numeroMotor}
                      onChange={e => setField("numeroMotor", e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="motorista">Motorista Principal</Label>
                    <Input
                      id="motorista"
                      maxLength={80}
                      value={form.motorista}
                      onChange={e => setField("motorista", e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="restricao">Restrição</Label>
                  <Input
                    id="restricao"
                    maxLength={80}
                    value={form.restricao}
                    onChange={e => setField("restricao", e.target.value)}
                  />
                </div>
              </TabsContent>

              {/* ── Tab: Seguros ──────────────────────────────────── */}
              <TabsContent value="seguros" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="seguradora">Seguradora</Label>
                    <Input
                      id="seguradora"
                      maxLength={60}
                      value={form.seguradora}
                      onChange={e => setField("seguradora", e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="exercicio">Exercício</Label>
                    <Input
                      id="exercicio"
                      type="number"
                      min={2000}
                      max={2100}
                      value={form.exercicio}
                      onChange={e => setField("exercicio", parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="valorCasco">Franquia / Casco (R$)</Label>
                    <Input
                      id="valorCasco"
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.valorCasco}
                      onChange={e => setField("valorCasco", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="danosMaterialValor">Danos Materiais (R$)</Label>
                    <Input
                      id="danosMaterialValor"
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.danosMaterialValor}
                      onChange={e => setField("danosMaterialValor", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="danosCorporaisValor">Danos Corporais (R$)</Label>
                    <Input
                      id="danosCorporaisValor"
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.danosCorporaisValor}
                      onChange={e => setField("danosCorporaisValor", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Salvando..." : "Salvar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* AlertDialog excluir */}
      <AlertDialog open={!!excluirId} onOpenChange={() => setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir veículo</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. Deseja excluir este veículo?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExcluir}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
